import React, { useState, useEffect, useRef, useMemo } from "react";
import axios from "axios";
import {
  downloadCrusherReportPdf,
  downloadIotDeviceReportPdf,
} from "./pdfgen.jsx";
import {
  ComposedChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { useParams, useNavigate } from "react-router-dom";
import { Calendar } from "lucide-react";

/** Native <input type="date"> — Safari-friendly: restores UA chrome, light scheme, visible calendar icon. */
function NativeDateInput({ id, value, onChange, "aria-label": ariaLabel, className = "" }) {
  return (
    <div className={`relative min-w-0 ${className}`}>
      <input
        id={id}
        type="date"
        value={value}
        onChange={onChange}
        aria-label={ariaLabel}
        className="w-full min-h-10 min-w-0 cursor-pointer rounded-lg border border-slate-300 bg-white py-2 pl-3 pr-10 text-sm text-slate-900 shadow-none outline-none appearance-auto [color-scheme:light] focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 sm:min-w-[11rem] [&::-webkit-calendar-picker-indicator]:absolute [&::-webkit-calendar-picker-indicator]:inset-y-0 [&::-webkit-calendar-picker-indicator]:right-0 [&::-webkit-calendar-picker-indicator]:z-[1] [&::-webkit-calendar-picker-indicator]:w-10 [&::-webkit-calendar-picker-indicator]:cursor-pointer [&::-webkit-calendar-picker-indicator]:opacity-0"
      />
      <Calendar
        className="pointer-events-none absolute right-2.5 top-1/2 z-0 h-4 w-4 -translate-y-1/2 text-slate-500"
        strokeWidth={2}
        aria-hidden
      />
    </div>
  );
}

const MetricsPage = () => {
  const { projectId } = useParams();
  const [project, setProject] = useState(null);
  const [devices, setDevices] = useState([]);
  const [selectedDevice, setSelectedDevice] = useState(null);
  const [deviceDataMap, setDeviceDataMap] = useState({});
  const [crusherTotalsMap, setCrusherTotalsMap] = useState({});
  const [selectedMetric, setSelectedMetric] = useState(null);
  const [showGraph, setShowGraph] = useState(false);
  const [showValueBoxes, setShowValueBoxes] = useState(false);
  const [chartData, setChartData] = useState([]);
  // dwonload popup code here
  const [showDownloadPopup, setShowDownloadPopup] = useState(false);
  const [rangeType, setRangeType] = useState("Today");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  const [selectedRange, setSelectedRange] = useState("Daily");
  const [customStart, setCustomStart] = useState("");
  const [customEnd, setCustomEnd] = useState("");

  // ✅ Waste Crusher States
  const [totalWasteToday, setTotalWasteToday] = useState(0);
  const [crusherRangeTotals, setCrusherRangeTotals] = useState({
    totalCrush: 0,
    totalOnTimeMinutes: 0,
  });

  const chartRef = useRef(null);
  const navigate = useNavigate();

  /** Horizontal chart panning only on narrow viewports (phones); tablet / laptop use full width. */
  const [chartHScrollEnabled, setChartHScrollEnabled] = useState(() => {
    if (typeof window === "undefined") return false;
    return window.matchMedia("(max-width: 767px)").matches;
  });

  useEffect(() => {
    if (typeof window === "undefined") return;
    const mq = window.matchMedia("(max-width: 767px)");
    const onChange = () => setChartHScrollEnabled(mq.matches);
    onChange();
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  const isCrusherDeviceCheck = (device) => {
    if (!device) return false;

    const id = String(device.deviceId || "").toLowerCase();
    const name = String(device.name || "").toLowerCase();

    return id.startsWith("20") && name.includes("crusher");
  };

  const formatCardSensorValue = (val) => {
    if (val === null || val === undefined || val === "-") return "-";
    const num = parseFloat(val);
    if (Number.isNaN(num)) return String(val);
    return num.toFixed(3);
  };

  const formatCardTimestamp = (ts) => {
    if (!ts) return null;
    try {
      return new Date(ts).toLocaleString();
    } catch {
      return null;
    }
  };

  useEffect(() => {
    if (!selectedMetric || !selectedDevice) return;

    if (selectedMetric === "crusher") return;

    if (selectedRange !== "Custom") {
      fetchGraphByRange(selectedRange);
    }
  }, [selectedRange, selectedMetric, selectedDevice]);

  const token = localStorage.getItem("token");

  // ✅ Step 1: Fetch Project Details (if needed)
  useEffect(() => {
    const fetchProject = async () => {
      try {
        const res = await axios.get(
          "https://api.livestockify.com/api/chennai/projects",
          {
            headers: { Authorization: `Bearer ${token}` },
          },
        );

        if (res.data && Array.isArray(res.data)) {
          const found = res.data.find((p) => p.projectUuid === projectId);
          if (found) {
            setProject(found);
            fetchDevices(found.projectUuid);
          }
        }
      } catch (err) {
        console.error("Error fetching project:", err);
      }
    };

    fetchProject();
  }, [projectId, token]);

  const fetchDevices = async (uuid) => {
    try {
      const res = await axios.get(
        `https://api.livestockify.com/api/chennai/projects/devices/${uuid}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );

      if (res.data?.devices && Array.isArray(res.data.devices)) {
        const deviceList = res.data.devices;
        setDevices(deviceList);

        // ✅ Auto select first device
        if (!selectedDevice) {
          setSelectedDevice(deviceList[0]);
        }

        deviceList.forEach(async (device) => {
          // Crusher device
          if (isCrusherDeviceCheck(device)) {
            fetchCrusherTotalForDevice(device.deviceId);
          }
          // Normal IoT device
          else {
            try {
              const res = await axios.get(
                `https://api.livestockify.com/api/chennai/iot/data/${device.deviceId}/1`,
                { headers: { Authorization: `Bearer ${token}` } },
              );

              if (res.data && Array.isArray(res.data) && res.data.length > 0) {
                const first = res.data[0];
                const sensors = { ...(first.sensors || {}) };
                if (first.timestamp != null && sensors.timestamp == null) {
                  sensors.timestamp = first.timestamp;
                }

                setDeviceDataMap((prev) => ({
                  ...prev,
                  [device.deviceId]: sensors,
                }));
              }
            } catch (err) {
              console.error("Error loading device card data:", err);
            }
          }
        });
      } else {
        setDevices([]);
      }
    } catch (err) {
      console.error("Error fetching devices:", err);
      setDevices([]);
    }
  };

  useEffect(() => {
    const fetchDeviceData = async () => {
      if (!selectedDevice) return;
      setShowGraph(false);
      setSelectedMetric(null);
      setChartData([]);
      // ✅ If Crusher Device

      if (isCrusherDeviceCheck(selectedDevice)) {
        setShowValueBoxes(true);
        await fetchCrusherTotal();
        return;
      }

      // ✅ Normal IoT Device
      try {
        const res = await axios.get(
          `https://api.livestockify.com/api/chennai/iot/data/${selectedDevice.deviceId}/1`,
          { headers: { Authorization: `Bearer ${token}` } },
        );

        if (res.data && Array.isArray(res.data) && res.data.length > 0) {
          const first = res.data[0];
          const sensors = { ...(first.sensors || {}) };
          if (first.timestamp != null && sensors.timestamp == null) {
            sensors.timestamp = first.timestamp;
          }

          setDeviceDataMap((prev) => ({
            ...prev,
            [selectedDevice.deviceId]: sensors,
          }));
        } else {
          setDeviceDataMap((prev) => ({
            ...prev,
            [selectedDevice.deviceId]: {},
          }));
        }

        setShowValueBoxes(true);
      } catch (err) {
        console.error("Error fetching IoT data:", err);

        setDeviceDataMap((prev) => ({
          ...prev,
          [selectedDevice.deviceId]: {},
        }));
      }
    };

    fetchDeviceData();
  }, [selectedDevice]);

  const handleDeviceClick = (device) => {
    setSelectedDevice(device);
    setShowValueBoxes(true);
  };

  // 🔹 Handle Metric click
  const handleMetricClick = (metricName) => {
    setSelectedMetric(metricName);
    setShowGraph(true);
    setSelectedRange("Daily");
  };

  const fetchGraphByRange = async (
    range,
    customStart = null,
    customEnd = null,
  ) => {
    if (!selectedMetric || !selectedDevice) return;

    let url = "";

    // ------------------------------
    if (range === "Daily") {
      url = `https://api.livestockify.com/api/chennai/iot/daily?deviceId=${selectedDevice.deviceId}`;
    } else if (range === "Weekly") {
      url = `https://api.livestockify.com/api/chennai/iot/week?deviceId=${selectedDevice.deviceId}`;
    } else if (range === "Monthly") {
      url = `https://api.livestockify.com/api/chennai/iot/month?deviceId=${selectedDevice.deviceId}`;
    } else if (range === "Custom" && customStart && customEnd) {
      const startParam = `${customStart}T00:00:00`;
      const endParam = `${customEnd}T23:59:59`;
      url = `https://api.livestockify.com/api/chennai/iot/custom?deviceId=${selectedDevice.deviceId}&start=${encodeURIComponent(startParam)}&end=${encodeURIComponent(endParam)}`;
    }

    try {
      const res = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const raw = res.data;
      const formatted = raw
        .map((item) => {
          const dateObj = new Date(item.timestamp);

          // convert to IST manually
          const istDate = new Date(
            dateObj.toLocaleString("en-US", { timeZone: "Asia/Kolkata" }),
          );

          return {
            day: istDate.toLocaleTimeString("en-IN", {
              hour: "2-digit",
              minute: "2-digit",
              hour12: false,
            }),
            fullDate: istDate.toLocaleString("en-GB", {
              day: "2-digit",
              month: "2-digit",
              // year: "numeric",
              hour: "2-digit",
              minute: "2-digit",
            }),
            fullTime: istDate.getTime(),
            value: item.sensors?.[selectedMetric] ?? null,
          };
        })
        .sort((a, b) => a.fullTime - b.fullTime);

      setChartData(formatted);
    } catch (error) {
      console.error("Error fetching graph data:", error);
    }
  };

  const handleDownloadPDF = () =>
    downloadIotDeviceReportPdf({
      token,
      device: selectedDevice,
      rangeType,
      startDate,
      endDate,
    });

  const handleCrusherDownloadPDF = () =>
    downloadCrusherReportPdf({
      device: selectedDevice,
      selectedRange,
      customStart,
      customEnd,
      crusherRangeTotals,
    });

  const fetchCrusherTotal = async (type = "today") => {
    if (!selectedDevice) return;

    try {
      const res = await axios.get(
        `https://api.livestockify.com/api/crusher-devices/totals?deviceId=${selectedDevice.deviceId}&type=${type}`,
      );

      if (res.data) {
        setCrusherTotalsMap((prev) => ({
          ...prev,
          [selectedDevice.deviceId]: {
            totalCrush: res.data.totalCrush ?? 0,
            totalOnTimeMinutes: res.data.totalOnTimeMinutes ?? 0,
          },
        }));
      }
    } catch (error) {
      console.error("Error fetching crusher total:", error);
    }
  };

  const fetchCrusherTotalForDevice = async (deviceId) => {
    try {
      const res = await axios.get(
        `https://api.livestockify.com/api/crusher-devices/totals?deviceId=${deviceId}&type=today`,
      );

      if (res.data) {
        setCrusherTotalsMap((prev) => ({
          ...prev,
          [deviceId]: {
            totalCrush: res.data.totalCrush ?? 0,
            totalOnTimeMinutes: res.data.totalOnTimeMinutes ?? 0,
          },
        }));
      }
    } catch (error) {
      console.error("Error fetching crusher total:", error);
    }
  };

  // ✅ Fetch Crusher Summary (Daily default)

  const fetchCrusherSummary = async (
    type = "today",
    start = null,
    end = null,
  ) => {
    if (!selectedDevice) return;

    try {
      let url = `https://api.livestockify.com/api/crusher-devices/summary?deviceId=${selectedDevice.deviceId}&type=${type}`;

      if (type === "custom" && start && end) {
        url += `&startDate=${start}&endDate=${end}`;
      }

      const res = await axios.get(url);

      if (!res.data?.graphData) return;

      setCrusherRangeTotals({
        totalCrush: res.data.totalCrush || 0,
        totalOnTimeMinutes: res.data.totalOnTimeMinutes || 0,
      });

      const formatted = (res.data.graphData || [])
        .map((item) => {
          const dateObj = new Date(item.timestamp);

          const istDate = new Date(
            dateObj.toLocaleString("en-US", { timeZone: "Asia/Kolkata" }),
          );

          return {
            day: istDate.toLocaleTimeString("en-IN", {
              hour: "2-digit",
              minute: "2-digit",
              hour12: false,
            }),
            fullDate: istDate.toLocaleString("en-GB", {
              day: "2-digit",
              month: "2-digit",
              // year: "numeric",
              hour: "2-digit",
              minute: "2-digit",
            }),
            fullTime: istDate.getTime(),
            totalCrush: Number(item.crush) || 0,
            totalOntime: Number(item.ontime) || 0,
          };
        })
        .sort((a, b) => a.fullTime - b.fullTime);

      setChartData(formatted);
      setShowGraph(true);
    } catch (error) {
      console.error("Crusher summary error:", error);
    }
  };

  const chartScrollWidthPx = useMemo(() => {
    if (!chartData?.length) return null;
    return Math.min(Math.max(chartData.length * 12 + 100, 320), 2800);
  }, [chartData]);

  const chartDotR = useMemo(() => {
    const n = chartData?.length ?? 0;
    if (n > 80) return 1.25;
    if (n > 40) return 1.75;
    return 2.5;
  }, [chartData]);

  /** Unique timestamps on X — avoids duplicate `day` labels stacking points and trapping the tooltip. */
  const formatChartAxisTick = useMemo(() => {
    return (timestamp) => {
      const d = new Date(timestamp);
      if (!Number.isFinite(d.getTime())) return "";
      if (selectedRange === "Monthly") {
        return d.toLocaleDateString("en-IN", {
          day: "2-digit",
          month: "short",
        });
      }
      if (selectedRange === "Weekly") {
        return d.toLocaleDateString("en-IN", {
          weekday: "short",
          day: "2-digit",
        });
      }
      return d.toLocaleTimeString("en-IN", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
      });
    };
  }, [selectedRange]);

  const [, setChartScrollTick] = useState(0);

  useEffect(() => {
    const el = chartRef.current;
    if (!el || !showGraph) return;
    const onScroll = () => setChartScrollTick((t) => t + 1);
    el.addEventListener("scroll", onScroll, { passive: true });
    return () => el.removeEventListener("scroll", onScroll);
  }, [showGraph, chartData.length]);

  return (
    <div className="min-h-screen w-full min-w-0 text-slate-900">
      <div className="flex w-full flex-col gap-4 sm:gap-5 lg:gap-6">
      <div className="text-sm text-black-600 flex items-center gap-2">
        {/* Dashboard Link */}
        <button
          onClick={() => navigate("/chennai/admin/dashboard")}
          className="font-semibold text-xl"
        >
          Dashboard
        </button>

        <span className="text-gray-400">›</span>

        <span className="font-semibold text-gray-800 text-xl">
          Project Details
        </span>
      </div>

      {/* 🔹 Project Header */}
      {project && (
        <div>
          <h2 className="text-2xl text-left sm:text-3xl font-bold text-[#22592A]">
            {project.projectName}
          </h2>
          <p className="text-black text-xl text-left">
            {project.projectLocation}
          </p>
        </div>
      )}

      {/* 🔹 Device Cards */}
      <div className="flex flex-wrap justify-start gap-4 sm:gap-5">
        {devices.length > 0 ? (
          devices.map((device) => {
            const live = deviceDataMap[device.deviceId];
            const lastUpdated = formatCardTimestamp(live?.timestamp);
            const sensorEntries = live
              ? Object.entries(live).filter(
                  ([key]) => key.toLowerCase() !== "timestamp",
                )
              : [];
            const hasSensorRows = sensorEntries.length > 0;

            return (
              <div
                key={device.deviceUuid}
                onClick={() => handleDeviceClick(device)}
                className={`group flex w-full max-w-[248px] shrink-0 cursor-pointer flex-col overflow-hidden rounded-2xl bg-gradient-to-br from-[#2ec155] via-[#34C759] to-emerald-700 shadow-lg shadow-emerald-900/20 ring-1 ring-white/25 transition duration-300 hover:-translate-y-1 hover:shadow-xl hover:shadow-emerald-900/25 sm:w-[248px] ${
                  selectedDevice?.deviceUuid === device.deviceUuid
                    ? "ring-2 ring-blue-500 ring-offset-2 ring-offset-white"
                    : ""
                }`}
              >
                <div className="flex items-start justify-between gap-2 px-4 py-3.5 text-white sm:px-5 sm:py-4">
                  <div className="min-w-0 flex flex-col items-start text-left">
                    <h3 className="text-base font-semibold leading-snug tracking-tight sm:text-lg">
                      {device.name || "Device"}
                    </h3>
                    <p className="mt-0.5 text-sm text-white/85">
                      Device ID: {device.deviceId}
                    </p>
                    {lastUpdated && (
                      <p className="mt-2 text-[11px] font-medium uppercase tracking-wide text-white/70">
                        Updated {lastUpdated}
                      </p>
                    )}
                  </div>
                </div>

                <div className="mt-auto px-3 pb-3 sm:px-4 sm:pb-4">
                  <div className="flex h-40 flex-col rounded-xl border border-white/40 bg-white/95 p-3 text-slate-800 shadow-inner backdrop-blur-sm sm:h-44 sm:p-4">
                    {device.name && (
                      <div className="mb-2 border-b border-slate-100 pb-2 text-xs font-semibold uppercase tracking-wide text-slate-500 sm:text-sm sm:normal-case sm:tracking-normal">
                        {device.name}
                      </div>
                    )}

                    <div className="flex flex-1 flex-col space-y-1 overflow-y-auto pr-1">
                      {isCrusherDeviceCheck(device) ? (
                        <>
                          <div className="flex items-center justify-between rounded-lg py-1.5 text-sm capitalize">
                            <span className="font-medium text-slate-600">
                              Total Crush
                            </span>
                            <span className="rounded-md bg-emerald-50 px-2.5 py-0.5 font-medium tabular-nums text-emerald-700">
                              {crusherTotalsMap[device.deviceId]?.totalCrush?.toFixed(
                                2,
                              ) || 0}
                            </span>
                          </div>
                          <div className="flex items-center justify-between rounded-lg py-1.5 text-sm capitalize">
                            <span className="font-medium text-slate-600">
                              Total On Time (Min)
                            </span>
                            <span className="rounded-md bg-emerald-50 px-2.5 py-0.5 font-medium tabular-nums text-emerald-700">
                              {crusherTotalsMap[device.deviceId]
                                ?.totalOnTimeMinutes || 0}
                            </span>
                          </div>
                        </>
                      ) : hasSensorRows ? (
                        sensorEntries.map(([key, value]) => (
                            <div
                              key={key}
                              className="flex items-center justify-between rounded-lg py-1.5 text-sm capitalize transition"
                            >
                              <span className="font-medium text-slate-600">
                                {key}
                              </span>
                              <span className="rounded-md bg-emerald-50 px-2.5 py-0.5 font-medium tabular-nums text-emerald-700">
                                {formatCardSensorValue(value)}
                              </span>
                            </div>
                          ))
                      ) : (
                        <p className="flex flex-1 items-center justify-center text-center text-sm text-slate-400">
                          Loading...
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })
        ) : (
          <p className="text-gray-500">
            No device data found for this project.
          </p>
        )}
      </div>

      {/* 🔹 Normal Device Metrics */}

      {showValueBoxes && selectedDevice && (
        <>
          {isCrusherDeviceCheck(selectedDevice) ? (
            // ✅ Crusher Device
            <div className="flex flex-wrap justify-start gap-4 sm:gap-5">
              <div
                onClick={() => {
                  setSelectedMetric("crusher");
                  setSelectedRange("Daily");
                  fetchCrusherSummary("today");
                }}
                className="w-full max-w-[248px] shrink-0 bg-white rounded-2xl shadow-md p-5 border border-gray-200 cursor-pointer hover:shadow-lg hover:border-[#0a5c33] transition sm:w-[248px]"
              >
                <div className="flex items-start justify-between gap-2">
                  <p className="text-gray-700 text-lg font-semibold capitalize">
                    Total Crush (Today)
                  </p>
                </div>

                <p className="text-[#4498D8] mt-3 text-left text-4xl font-bold leading-tight sm:text-5xl">
                  {crusherTotalsMap[
                    selectedDevice.deviceId
                  ]?.totalCrush?.toFixed(2) || 0}{" "}
                  <span className="text-lg font-semibold text-gray-500 sm:text-xl">
                    kg
                  </span>
                </p>
              </div>

              <div
                onClick={() => {
                  setSelectedMetric("crusher");
                  setSelectedRange("Daily"); // default
                  fetchCrusherSummary("today");
                }}
                className="w-full max-w-[248px] shrink-0 bg-white rounded-2xl shadow-md p-5 border border-gray-200 cursor-pointer hover:shadow-lg hover:border-[#0a5c33] transition sm:w-[248px]"
              >
                <div className="flex items-start justify-between gap-2">
                  <p className="text-gray-700 text-lg font-semibold capitalize">
                    On Time (Minutes)
                  </p>
                </div>

                <p className="text-[#4498D8] mt-3 text-left text-4xl font-bold leading-tight sm:text-5xl">
                  {crusherTotalsMap[selectedDevice.deviceId]
                    ?.totalOnTimeMinutes || 0}{" "}
                  <span className="text-lg font-semibold text-gray-500 sm:text-xl">
                    min
                  </span>
                </p>
              </div>
            </div>
          ) : (
            // ✅ Normal IoT Device
            deviceDataMap[selectedDevice.deviceId] && (
              <div className="w-full min-w-0">
                <h3 className="mb-3 text-left text-xs font-bold uppercase tracking-[0.12em] text-slate-800 sm:text-sm">
                  Sensors
                </h3>
                <div className="-mx-1 snap-x snap-mandatory overflow-x-auto overflow-y-hidden pb-2 [-webkit-overflow-scrolling:touch] sm:mx-0 sm:snap-none sm:overflow-visible sm:pb-0">
                  <div className="flex flex-nowrap gap-3 px-1 sm:flex-wrap sm:justify-start sm:gap-5 sm:px-0">
                    {Object.entries(deviceDataMap[selectedDevice.deviceId])
                      .filter(([key]) => key.toLowerCase() !== "timestamp")
                      .map(([key, value]) => (
                    <div
                      key={key}
                      onClick={() => handleMetricClick(key)}
                      className="min-w-[156px] w-[156px] shrink-0 snap-start rounded-xl border border-gray-200 bg-white p-3 shadow-md transition hover:border-[#0a5c33] hover:shadow-lg cursor-pointer sm:min-w-[248px] sm:w-[248px] sm:rounded-2xl sm:p-5"
                    >
                      <div className="flex items-start justify-between gap-1.5">
                        <p className="text-sm font-semibold capitalize leading-snug text-gray-700 sm:text-lg">
                          {key}
                        </p>
                      </div>

                      <p className="mt-1.5 text-left text-2xl font-bold leading-tight text-[#4498D8] sm:mt-3 sm:text-4xl sm:leading-tight lg:text-5xl">
                        {typeof value === "number" ? value.toFixed(2) : value}
                        <span className="ml-0.5 text-sm font-semibold text-gray-500 sm:ml-1.5 sm:text-lg lg:text-xl">
                          {key.toLowerCase() === "ph"
                            ? "pH"
                            : key.toLowerCase() === "temperature"
                              ? "°C"
                              : ""}
                        </span>
                      </p>
                    </div>
                      ))}
                  </div>
                </div>
              </div>
            )
          )}
        </>
      )}

      {/* 🔹 Graph Section */}
      {showGraph && (
        <div className="bg-white rounded-2xl shadow-md border border-gray-200 p-4 sm:p-6">
          {selectedRange === "Custom" && (
            <div className="mb-5 mt-1 flex w-full min-w-0 flex-col gap-3 sm:mt-0 sm:flex-row sm:items-end sm:gap-4">
              <div className="flex w-full min-w-0 flex-1 flex-col gap-3 sm:flex-row sm:gap-4">
                <div className="flex min-w-0 flex-1 flex-col">
                  <label
                    htmlFor="metrics-graph-custom-start"
                    className="mb-1 text-left text-sm text-slate-600"
                  >
                    Start Date
                  </label>
                  <NativeDateInput
                    id="metrics-graph-custom-start"
                    value={customStart}
                    onChange={(e) => setCustomStart(e.target.value)}
                  />
                </div>

                <div className="flex min-w-0 flex-1 flex-col">
                  <label
                    htmlFor="metrics-graph-custom-end"
                    className="mb-1 text-left text-sm text-slate-600"
                  >
                    End Date
                  </label>
                  <NativeDateInput
                    id="metrics-graph-custom-end"
                    value={customEnd}
                    onChange={(e) => setCustomEnd(e.target.value)}
                  />
                </div>
              </div>

              <button
                type="button"
                className="w-full shrink-0 rounded-md bg-blue-600 px-4 py-2.5 text-sm font-medium text-white transition hover:bg-blue-700 sm:w-auto sm:self-end"
                onClick={() => {
                  if (selectedMetric === "crusher") {
                    fetchCrusherSummary("custom", customStart, customEnd);
                  } else {
                    fetchGraphByRange("Custom", customStart, customEnd);
                  }
                }}
              >
                Apply
              </button>
            </div>
          )}
          <div className="mb-4 w-full min-w-0 space-y-2.5">
            <div className="flex w-full min-w-0 items-center justify-between gap-2">
              <h3 className="min-w-0 flex-1 basis-0 truncate text-left text-base font-semibold capitalize text-slate-900 sm:text-lg">
                {selectedMetric === "crusher"
                  ? "Crusher Summary"
                  : selectedMetric}
              </h3>

              <select
                value={selectedRange}
                aria-label="Graph date range"
                className="ml-2 shrink-0 rounded-md border border-slate-300 bg-white py-1 pl-2 pr-6 text-xs text-slate-800 shadow-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/25 sm:text-sm sm:py-1.5 sm:pl-2.5 sm:pr-7"
                onChange={(e) => {
                  const value = e.target.value;
                  setSelectedRange(value);

                  if (selectedMetric === "crusher") {
                    let type = "today";

                    if (value === "Weekly") type = "weekly";
                    else if (value === "Monthly") type = "monthly";
                    else if (value === "Daily") type = "today";

                    fetchCrusherSummary(type);
                    fetchCrusherTotal("today");
                  } else {
                    fetchGraphByRange(value);
                  }
                }}
              >
                <option value="Daily">Daily</option>
                <option value="Weekly">Weekly</option>
                <option value="Monthly">Monthly</option>
                <option value="Custom">Custom</option>
              </select>
            </div>

            {selectedMetric === "crusher" && (
              <div className="flex w-full flex-wrap items-center justify-between gap-x-6 gap-y-1 border-t border-slate-100 pt-2.5 sm:justify-center sm:gap-x-10 sm:pt-2">
                <div className="min-w-0 text-left sm:text-center">
                  <p className="text-xs text-gray-500">Total Crush</p>
                  <p className="text-base font-bold text-[#34C759] sm:text-lg">
                    {crusherRangeTotals.totalCrush.toFixed(2)} Kg
                  </p>
                </div>

                <div className="min-w-0 text-left sm:text-center">
                  <p className="text-xs text-gray-500">Total On Time</p>
                  <p className="text-base font-bold text-blue-600 sm:text-lg">
                    {crusherRangeTotals.totalOnTimeMinutes} Min
                  </p>
                </div>
              </div>
            )}
          </div>

          <div
            ref={chartRef}
            className={`w-full min-w-0 overflow-y-hidden rounded-xl border border-slate-100 bg-slate-50/40 pb-1 [-webkit-tap-highlight-color:transparent] outline-none focus:outline-none focus-visible:outline-none ${
              chartHScrollEnabled ? "overflow-x-auto" : "overflow-x-hidden"
            }`}
          >
            <div
              className="h-[260px] min-h-[240px] sm:h-[300px] outline-none [-webkit-tap-highlight-color:transparent] [&_.recharts-cartesian-axis-tick_text]:outline-none [&_.recharts-layer]:outline-none [&_.recharts-responsive-container]:outline-none [&_.recharts-surface]:outline-none [&_.recharts-surface]:ring-0 [&_.recharts-surface:focus]:outline-none [&_.recharts-surface:focus-visible]:outline-none [&_.recharts-tooltip-cursor]:hidden [&_.recharts-wrapper]:outline-none [&_.recharts-wrapper]:ring-0 [&_.recharts-wrapper:focus]:outline-none [&_.recharts-wrapper:focus-visible]:outline-none [&_g]:outline-none [&_path]:outline-none [&_rect]:outline-none [&_svg]:outline-none [&_svg]:ring-0 [&_svg:focus]:outline-none [&_svg:focus-visible]:outline-none"
              style={
                chartHScrollEnabled && chartScrollWidthPx
                  ? {
                      width: `${chartScrollWidthPx}px`,
                      minWidth: `${chartScrollWidthPx}px`,
                    }
                  : { width: "100%", minWidth: 0 }
              }
            >
              {chartData.length === 0 ? (
                <div className="flex h-full items-center justify-center text-sm text-slate-500">
                  No data for this range.
                </div>
              ) : (
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart
                  data={chartData}
                  accessibilityLayer={false}
                  margin={{
                    top: 8,
                    right: selectedMetric === "crusher" ? 28 : 8,
                    left: selectedMetric === "crusher" ? 4 : 0,
                    bottom: 8,
                  }}
                >
                  <defs>
                    <linearGradient id="metricFill" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#0ea5e9" stopOpacity={0.45} />
                      <stop offset="100%" stopColor="#0ea5e9" stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id="crushFill" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#22c55e" stopOpacity={0.42} />
                      <stop offset="100%" stopColor="#22c55e" stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id="ontimeFill" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#38bdf8" stopOpacity={0.35} />
                      <stop offset="100%" stopColor="#38bdf8" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid
                    strokeDasharray="3 3"
                    stroke="#e2e8f0"
                    vertical={false}
                  />
                  <XAxis
                    dataKey="fullTime"
                    type="number"
                    scale="time"
                    domain={["dataMin", "dataMax"]}
                    tick={{ fontSize: 10, fill: "#64748b" }}
                    tickFormatter={formatChartAxisTick}
                    tickLine={false}
                    axisLine={false}
                    minTickGap={40}
                  />
                  <YAxis
                    yAxisId="left"
                    width={selectedMetric === "crusher" ? 40 : 32}
                    tickCount={5}
                    tick={{ fontSize: 10, fill: "#64748b", dx: -2 }}
                    tickLine={false}
                    axisLine={false}
                    domain={["auto", "auto"]}
                    tickFormatter={(v) =>
                      typeof v === "number" && Number.isFinite(v)
                        ? String(Number(v.toFixed(2)))
                        : String(v ?? "")
                    }
                  />
                  {selectedMetric === "crusher" && (
                    <YAxis
                      yAxisId="right"
                      orientation="right"
                      width={36}
                      tickCount={5}
                      tick={{ fontSize: 10, fill: "#64748b" }}
                      tickLine={false}
                      axisLine={false}
                      domain={["auto", "auto"]}
                      tickFormatter={(v) =>
                        typeof v === "number" && Number.isFinite(v)
                          ? String(Number(v.toFixed(1)))
                          : String(v ?? "")
                      }
                    />
                  )}
                  <Tooltip
                    cursor={false}
                    allowEscapeViewBox={{ x: false, y: false }}
                    wrapperStyle={{ outline: "none", boxShadow: "none", zIndex: 20 }}
                    contentStyle={{
                      borderRadius: "12px",
                      border: "1px solid #e2e8f0",
                      fontSize: "12px",
                      outline: "none",
                      boxShadow: "0 4px 6px -1px rgb(0 0 0 / 0.07)",
                    }}
                    labelFormatter={(_label, payload) => {
                      if (payload?.length > 0) {
                        return payload[0].payload.fullDate;
                      }
                      return _label;
                    }}
                    formatter={(value, name) => {
                      if (
                        selectedMetric === "crusher" &&
                        name === "Total Crush"
                      ) {
                        return [Number(value).toFixed(2), name];
                      }
                      if (
                        selectedMetric === "crusher" &&
                        name === "On time (min)"
                      ) {
                        return [Number(value).toFixed(1), name];
                      }
                      return [value, name];
                    }}
                  />
                  {selectedMetric === "crusher" && (
                    <Legend
                      wrapperStyle={{ fontSize: "12px", paddingTop: 4 }}
                      verticalAlign="top"
                      align="right"
                    />
                  )}

                  {selectedMetric === "crusher" ? (
                    <>
                      <Area
                        yAxisId="left"
                        type="monotone"
                        dataKey="totalCrush"
                        name="Total Crush"
                        stroke="#16a34a"
                        strokeWidth={2}
                        fill="url(#crushFill)"
                        dot={{ r: chartDotR, fill: "#15803d" }}
                        activeDot={{
                          r: 5,
                          stroke: "none",
                          strokeWidth: 0,
                          fill: "#15803d",
                        }}
                        isAnimationActive={false}
                      />
                      <Area
                        yAxisId="right"
                        type="monotone"
                        dataKey="totalOntime"
                        name="On time (min)"
                        stroke="#0284c7"
                        strokeWidth={2}
                        fill="url(#ontimeFill)"
                        dot={{ r: chartDotR, fill: "#0369a1" }}
                        activeDot={{
                          r: 5,
                          stroke: "none",
                          strokeWidth: 0,
                          fill: "#0369a1",
                        }}
                        isAnimationActive={false}
                      />
                    </>
                  ) : (
                    <Area
                      yAxisId="left"
                      type="monotone"
                      dataKey="value"
                      name={String(selectedMetric ?? "value")}
                      stroke="#0284c7"
                      strokeWidth={2}
                      fill="url(#metricFill)"
                      dot={{ r: chartDotR, fill: "#0369a1" }}
                      activeDot={{
                        r: 5,
                        stroke: "none",
                        strokeWidth: 0,
                        fill: "#0369a1",
                      }}
                      connectNulls={false}
                      isAnimationActive={false}
                    />
                  )}
                </ComposedChart>
              </ResponsiveContainer>
              )}
            </div>
          </div>

          {true && (
            <div className="text-center mt-4">
              <button
                onClick={() => setShowDownloadPopup(true)}
                className="bg-blue-500 text-white px-6 py-2 rounded-lg font-medium hover:bg-blue-600 transition"
              >
                Download Report
              </button>
            </div>
          )}
        </div>
      )}

      {/* ⭐ DOWNLOAD REPORT POPUP ⭐ */}
      {showDownloadPopup && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/35 px-4 backdrop-blur-[2px]">
          <div className="w-full max-w-md rounded-2xl border border-slate-200/80 bg-white/95 p-5 shadow-2xl shadow-slate-900/15 sm:p-6">
            <div className="mb-4 flex items-start justify-between gap-3">
              <div className="min-w-0">
                <h3 className="text-lg font-semibold text-slate-900">
                  Download Report
                </h3>
                <p className="mt-1 truncate text-sm text-slate-500">
                  {selectedDevice?.name || "Device"}
                </p>
              </div>
              <span className="rounded-full bg-slate-100 px-2.5 py-1 text-xs font-semibold uppercase tracking-wide text-slate-600">
                {selectedMetric || "Sensor"}
              </span>
            </div>

            <div className="mb-4">
              <p className="mb-2 text-xs font-semibold uppercase tracking-[0.12em] text-slate-500">
                Range
              </p>
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                {["Today", "Weekly", "Monthly", "Custom"].map((r) => (
                  <button
                    key={r}
                    type="button"
                    className={`rounded-lg border px-3 py-2 text-sm font-medium transition ${
                      rangeType === r
                        ? "border-blue-500 bg-blue-500 text-white shadow-sm shadow-blue-500/25"
                        : "border-slate-200 bg-slate-50 text-slate-700 hover:bg-slate-100"
                    }`}
                    onClick={() => setRangeType(r)}
                  >
                    {r}
                  </button>
                ))}
              </div>
            </div>

            {rangeType === "Custom" && (
              <div className="mb-5 rounded-xl border border-slate-200 bg-slate-50/80 p-3">
                <p className="mb-2 text-xs font-semibold uppercase tracking-[0.12em] text-slate-500">
                  Custom Date Range
                </p>
                <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                  <NativeDateInput
                    id="metrics-pdf-start"
                    aria-label="Report start date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                  />
                  <NativeDateInput
                    id="metrics-pdf-end"
                    aria-label="Report end date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                  />
                </div>
              </div>
            )}

            <div className="flex gap-2.5">
              <button
                type="button"
                className="flex-1 rounded-lg bg-blue-500 py-2.5 text-sm font-semibold text-white transition hover:bg-blue-600"
                onClick={() => {
                  setShowDownloadPopup(false);

                  if (selectedMetric === "crusher") {
                    handleCrusherDownloadPDF();
                  } else {
                    handleDownloadPDF();
                  }
                }}
              >
                Download Report
              </button>
              <button
                type="button"
                className="rounded-lg border border-slate-300 px-4 py-2.5 text-sm font-semibold text-slate-700 transition hover:bg-slate-100"
                onClick={() => setShowDownloadPopup(false)}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
      </div>
    </div>
  );
};

export default MetricsPage;
